import angularModule from 'MobileAppRateHelper/angularModule/scripts/mobile_app_rate_helper_module';
import moment from 'moment-timezone';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { AppReview } from '@capawesome/capacitor-app-review';
import { targetBrandConfig } from 'AppBranding';

angularModule.factory('MobileAppRateHelper', [
    '$injector',
    $injector => {
        const $q = $injector.get('$q');
        const EventLogger = $injector.get('EventLogger');
        const ErrorLogService = $injector.get('ErrorLogService');

        return {
            APP_RATING_PROMPT_COUNTER: 'app_rating_prompt_counter',
            LAST_APP_RATING_PROMPT_AT: 'last_app_rating_prompt_at',
            ELIGIBLE_LAUNCH_COUNTER: 'app_rating_eligible_launch_counter',
            ELIGIBLE_LAUNCHES_BEFORE_PROMPT: 3,

            // See https://trello.com/c/JqfEZzSm and https://trello.com/c/E64eHApu for more information
            meetsRequirementsForMobileAppRatingPrompt(user) {
                // There are certain states the user can be when we don’t want to prompt them for an app rating,
                // so we check for those first since they don’t require an API request.
                if (
                    !Capacitor.isNativePlatform() ||
                    !user ||
                    !targetBrandConfig(user).supportsMobileAppRateHelper ||
                    user.hasExternalInstitution ||
                    user.isDemo ||
                    !user.hasEverApplied ||
                    (user.hasEverBeenRejected && !user.isCurrentOrHasCompletedProgram)
                ) {
                    return $q.when(false);
                }

                // This last step houses all of the asynchronous checks (some of which may require an API call),
                // which is why this step is separated from the rest and done last.
                return $q
                    .all([
                        user.progress.getAllProgress(),
                        this.getAppRatingPromptCounter(),
                        this.getLastAppRatingPromptAt(),
                    ])
                    .then(responses => {
                        const streamProgresses = responses[0].streamProgress;
                        const completeStream = streamProgresses.find(streamProgress => streamProgress.complete);
                        const appRatingPromptCounter = responses[1];
                        const lastAppRatingPromptAt = responses[2];
                        return (
                            !!completeStream &&
                            appRatingPromptCounter < 3 &&
                            (!lastAppRatingPromptAt || moment().subtract(30, 'days').toDate() >= lastAppRatingPromptAt)
                        );
                    });
            },

            // The OS decides whether the review prompt actually appears and does not tell us, so the
            // counter tracks requests made, not prompts shown.
            promptForRatingIfEligible(user) {
                const recordRequest = () =>
                    $q.all([
                        this.incrementAppRatingPromptCounter(),
                        this.setLastAppRatingPromptAt(),
                        this.setEligibleLaunchCounter(0),
                    ]);

                return this.meetsRequirementsForMobileAppRatingPrompt(user)
                    .then(eligible => {
                        if (!eligible) return undefined;

                        return this.incrementEligibleLaunchCounter().then(eligibleLaunches => {
                            if (eligibleLaunches < this.ELIGIBLE_LAUNCHES_BEFORE_PROMPT) return undefined;

                            // A failed request still counts, so a device without Google Play is not asked at every launch.
                            return $q.when(AppReview.requestReview()).then(
                                () =>
                                    recordRequest().then(([counter]) => {
                                        EventLogger.log('mobile_app_rating:requested', { counter });
                                    }),
                                error => recordRequest().then(() => $q.reject(error)),
                            );
                        });
                    })
                    .catch(error => {
                        ErrorLogService.notify('Failed to request a mobile app review', undefined, {
                            level: 'warning',
                            error,
                        });
                    });
            },

            getCounter(key) {
                return $q.when(Preferences.get({ key })).then(({ value }) => parseInt(value, 10) || 0);
            },

            getAppRatingPromptCounter() {
                return this.getCounter(this.APP_RATING_PROMPT_COUNTER);
            },

            setAppRatingPromptCounter(counter) {
                return $q.when(Preferences.set({ key: this.APP_RATING_PROMPT_COUNTER, value: String(counter) }));
            },

            incrementAppRatingPromptCounter() {
                return this.getAppRatingPromptCounter().then(counter => {
                    const newCounter = counter + 1;
                    return this.setAppRatingPromptCounter(newCounter).then(() => newCounter);
                });
            },

            incrementEligibleLaunchCounter() {
                return this.getCounter(this.ELIGIBLE_LAUNCH_COUNTER).then(counter => {
                    const eligibleLaunches = counter + 1;
                    return this.setEligibleLaunchCounter(eligibleLaunches).then(() => eligibleLaunches);
                });
            },

            setEligibleLaunchCounter(eligibleLaunches) {
                return $q.when(Preferences.set({ key: this.ELIGIBLE_LAUNCH_COUNTER, value: String(eligibleLaunches) }));
            },

            getLastAppRatingPromptAt() {
                return $q
                    .when(Preferences.get({ key: this.LAST_APP_RATING_PROMPT_AT }))
                    .then(({ value }) => (value === null ? null : new Date(Number(value))));
            },

            setLastAppRatingPromptAt() {
                return $q.when(
                    Preferences.set({
                        key: this.LAST_APP_RATING_PROMPT_AT,
                        value: String(Date.now()),
                    }),
                );
            },
        };
    },
]);
